import { HistoryItem, AspectRatio } from "../types";

const API_URL = import.meta.env.VITE_API_URL || '';

const VIDEO_GENERATION_MESSAGES = [
    "Warming up the cameras...",
    "Adjusting the lighting...",
    "Directing the digital model...",
    "Sending rushes to the editing room...",
    "Applying final color grading...",
    "Rendering the final cut...",
    "Almost ready for the premiere!",
];

export const editImage = async (
  baseImage: HistoryItem,
  prompt: string,
  baseInstructions: string
): Promise<{ data: string; mimeType: string }> => {
  const response = await fetch(`${API_URL}/api/image/edit`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      imageUrl: baseImage.imageUrl,
      mimeType: baseImage.mimeType,
      prompt: prompt,
      baseInstructions: baseInstructions,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to edit image');
  }

  const data = await response.json();
  return {
    data: data.data,
    mimeType: data.mimeType,
  };
};

export const generateVideo = async (
  startImage: HistoryItem,
  prompt: string,
  aspectRatio: AspectRatio,
  onStatusUpdate: (status: string) => void
): Promise<string> => {
    onStatusUpdate("Starting video generation...");
    
    // Initiate video generation
    const generateResponse = await fetch(`${API_URL}/api/video/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        imageUrl: startImage.imageUrl,
        mimeType: startImage.mimeType,
        prompt: prompt,
        aspectRatio: aspectRatio,
      }),
    });

    if (!generateResponse.ok) {
      const error = await generateResponse.json();
      throw new Error(error.error || 'Failed to generate video');
    }

    let operationData = await generateResponse.json();
    let messageIndex = 0;

    // Poll for completion
    while (!operationData.done) {
      onStatusUpdate(VIDEO_GENERATION_MESSAGES[messageIndex % VIDEO_GENERATION_MESSAGES.length]);
      messageIndex++;
      await new Promise(resolve => setTimeout(resolve, 10000));
      
      const pollResponse = await fetch(`${API_URL}/api/video/poll`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          operationName: operationData.operationName,
        }),
      });

      if (!pollResponse.ok) {
        const error = await pollResponse.json();
        throw new Error(error.error || 'Failed to poll video status');
      }

      operationData = await pollResponse.json();
    }

    if (!operationData.downloadLink) {
        throw new Error("Video generation completed, but no download link was found.");
    }

    onStatusUpdate("Fetching generated video...");
    
    // Download video through proxy
    const videoResponse = await fetch(
      `${API_URL}/api/video/download?url=${encodeURIComponent(operationData.downloadLink)}`,
      {
        credentials: 'include',
      }
    );
    
    if (!videoResponse.ok) {
        throw new Error(`Failed to fetch video: ${videoResponse.statusText}`);
    }
    
    const videoBlob = await videoResponse.blob();
    return URL.createObjectURL(videoBlob);
};
